import { FC, useEffect, useState } from 'react';
import { useHistory, Link } from 'react-router-dom';
import * as z from 'zod';
import toast from 'react-hot-toast';

import { useAuth } from '@hooks/Auth';

import InputText from '@components/elements/InputText';
import ButtonMighty from '@components/elements/ButtonMighty';
import Loader from '@components/elements/Loader';

import './VerifyPage.css';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

const FORM_SCHEMA = z.object({
  username: z.string().nonempty(),
  code: z.string().nonempty(),
});

type FormInput = z.infer<typeof FORM_SCHEMA>;

const VerifyPage: FC = () => {
  const history = useHistory<any>();
  const [requestingCode, setRequestingCode] = useState<boolean>(false);

  const { user, resendSignupCode, confirmSignup } = useAuth();

  const {
    handleSubmit,
    reset,
    register,
    watch,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm<FormInput>({
    resolver: zodResolver(FORM_SCHEMA),
  });

  useEffect(() => {
    if (user?.attributes.email_verified) history.replace('/feed');
    else if (user) {
      reset({
        username: user.username,
      });
    }
  }, [user, history, reset]);

  useEffect(() => {
    if (user) {
      reset({
        username: user.username,
      });
    }
  }, [reset, user]);

  useEffect(() => {
    const params = new URLSearchParams(history.location.search);
    const username = params.get('username');
    if (username) reset({ username });
  }, [history.location.search, reset]);

  const onSubmit = async (data: FormInput) => {
    console.log(data);
    try {
      const res = await confirmSignup(data.username, data.code);
      toast.success('Successfully verified your account.');
      console.log(res);
      //history.replace('/feed');
      history.replace('/login');
    } catch (error: any) {
      if (
        error.message ===
        'User cannot be confirmed. Current status is CONFIRMED'
      ) {
        toast.success('Successfully verified your account.');
        //history.replace('/feed');
        history.replace('/login');
      } else {
        toast.error(error.message);
        console.log('Request code error', { error });
      }
    }
  };

  const resendCode = async (username: string) => {
    try {
      await FORM_SCHEMA.pick({
        username: true,
      }).parseAsync({
        username,
      });
      const res = await resendSignupCode(username);
      console.log(res);
      if (res.CodeDeliveryDetails) {
        toast.success(
          `Sent confirmation code to your ${res.CodeDeliveryDetails.DeliveryMedium.toString().toLowerCase()}`
        );
      } else {
        toast.success(`Sent confirmation code.`);
      }
    } catch (error: any) {
      if (error.message === 'User is already confirmed.') {
        toast.success('Your account is already verified.');
        history.replace('/home');
      } else {
        console.log('Request code error', { error });
      }
    }
  };

  let content;
  if (isSubmitting)
    content = (
      <Loader
        title="Submitting verification code..."
        description="This may take awhile to verify, please don't close this page."
      />
    );
  else {
    content = (
      <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
        <p className="mb-4 text-sm">
          Please check your email for the verification code
        </p>

        <InputText placeholder="Username" {...register('username')} />
        {errors.username?.message && (
          <div className="text-sm font-bold text-red">
            {errors.username?.message}
          </div>
        )}
        <InputText placeholder="Verification Code" {...register('code')} />
        {errors.code?.message && (
          <div className="text-sm font-bold text-red">
            {errors.code?.message}
          </div>
        )}

        <div className="flex flex-col items-center pt-8 space-y-4">
          <div>
            <ButtonMighty
              type="submit"
              disabled={isSubmitting}
              loading={isSubmitting}
              color="#ff1dfc"
              text="verify"
              width="100%"
              borderColor="#00ff43"
              margin="0"
              textColor="#fff"
              onClick={() => handleSubmit(onSubmit)()}
            />
          </div>

          <div>
            <ButtonMighty
              type="button"
              disabled={isSubmitting || requestingCode}
              loading={isSubmitting || requestingCode}
              color="#221f20"
              width="100%"
              text="Resend verification code"
              borderColor="#f97075"
              margin="0"
              textColor="#fff"
              onClick={async () => {
                setRequestingCode(true);
                try {
                  trigger('username');
                  await resendCode(watch('username'));
                } catch (err: any) {
                  console.error(err);
                  toast.error(err.message);
                }
                setRequestingCode(false);
              }}
            />
          </div>

          <Link to="/signup">
            <div
              role="button"
              className="font-semibold text-center font-display text-blue"
            >
              Don&apos;t have an account?
              <br />
              Join MegaX World
            </div>
          </Link>
        </div>
      </form>
    );
  }

  return (
    <div className="w-full min-h-screen pt-20 verify-page-background">
      <div className="container mx-auto md:w-108">
        <div className="px-4 py-4 mx-4 bg-white lg:px-8 lg:mx-0">
          <h1 className="mt-8 mb-2 text-5xl font-bold">Verify email</h1>
          <div>{content}</div>
        </div>
      </div>
    </div>
  );
};

export default VerifyPage;
